import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, CheckCircle2, LockKeyhole } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuthDialog } from "../contexts/AuthDialogContext";
import {
  confirmFirebasePasswordReset,
  verifyFirebasePasswordResetCode,
} from "../services/firebaseAuth";

const ResetPasswordPage = () => {
  const { t } = useLanguage();
  const { openAuth } = useAuthDialog();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetCodeFromUrl = searchParams.get("oobCode") || searchParams.get("token") || "";

  const [formData, setFormData] = useState({
    resetCode: resetCodeFromUrl,
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");

  useEffect(() => {
    if (resetCodeFromUrl) {
      setFormData((current) => ({
        ...current,
        resetCode: current.resetCode || resetCodeFromUrl,
      }));
    }
  }, [resetCodeFromUrl]);

  useEffect(() => {
    let isMounted = true;

    const verifyCode = async () => {
      if (!resetCodeFromUrl) {
        return;
      }

      try {
        const email = await verifyFirebasePasswordResetCode(resetCodeFromUrl);
        if (isMounted) {
          setVerifiedEmail(email || "");
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(
            error?.message || t("auth.invalidResetLink", "This reset link is invalid or expired."),
          );
        }
      }
    };

    verifyCode();

    return () => {
      isMounted = false;
    };
  }, [resetCodeFromUrl, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (!formData.resetCode.trim()) {
      setErrorMessage(t("auth.enterResetToken", "Enter the reset code from your email."));
      return;
    }

    if (!formData.newPassword || !formData.confirmPassword) {
      setErrorMessage(t("auth.fillAllFields", "Please fill all fields."));
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setErrorMessage(t("auth.passwordMismatch", "Passwords do not match."));
      return;
    }

    if (formData.newPassword.length < 6) {
      setErrorMessage(t("auth.passwordTooShort", "Password must be at least 6 characters."));
      return;
    }

    setLoading(true);
    try {
      await confirmFirebasePasswordReset(
        formData.resetCode.trim(),
        formData.newPassword,
      );
      setSuccess(true);
    } catch (error) {
      setErrorMessage(error.message || t("auth.resetError", "Failed to reset password."));
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = () => {
    navigate("/");
    openAuth("login");
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] py-16">
      <div className="mx-auto w-full max-w-lg px-4">
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#D4AF37]"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("auth.backToHome", "Back to home")}
        </Link>

        <div className="rounded-3xl border border-black/5 bg-white p-8 shadow-sm dark:border-white/10 dark:bg-gray-900">
          {success ? (
            <div className="space-y-6 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10 text-green-500">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <div className="space-y-2">
                <h1 className="font-[var(--font-display)] text-3xl font-bold text-gray-900 dark:text-white">
                  {t("auth.passwordUpdated", "Password updated successfully")}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("auth.signInWithNewPassword", "Sign in with your new password to continue.")}
                </p>
              </div>
              <div className="space-y-3">
                <Button onClick={handleSignIn} className="w-full btn-gold text-black">
                  {t("auth.signIn", "Sign In")}
                </Button>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/">{t("auth.backToHome", "Back to home")}</Link>
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-8 space-y-3 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#D4AF37]/10 text-[#D4AF37]">
                  <LockKeyhole className="h-8 w-8" />
                </div>
                <h1 className="font-[var(--font-display)] text-3xl font-bold text-gray-900 dark:text-white">
                  {t("auth.resetPassword", "Reset Password")}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t(
                    "auth.resetPageDescription",
                    "Choose a new password for your Velaris account.",
                  )}
                </p>
              </div>

              {!resetCodeFromUrl && (
                <div className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-700 dark:text-amber-200">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                  <p>
                    {t(
                      "auth.invalidResetLink",
                      "This reset link is missing a reset code. Request a new reset email.",
                    )}
                  </p>
                </div>
              )}

              {verifiedEmail && (
                <div className="mb-6 rounded-2xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-700 dark:text-green-200">
                  {t("auth.resettingPasswordFor", "Resetting password for")} {verifiedEmail}
                </div>
              )}

              {errorMessage && (
                <div className="mb-6 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-300">
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("auth.resetToken", "Reset Code")}
                  </label>
                  <Input
                    value={formData.resetCode}
                    onChange={(event) => setFormData((current) => ({
                      ...current,
                      resetCode: event.target.value,
                    }))}
                    placeholder={t("auth.enterResetToken", "Paste the reset code from your email")}
                    autoComplete="one-time-code"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("form.newPassword", "New Password")}
                  </label>
                  <Input
                    type="password"
                    value={formData.newPassword}
                    onChange={(event) => setFormData((current) => ({
                      ...current,
                      newPassword: event.target.value,
                    }))}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("form.confirmPassword", "Confirm Password")}
                  </label>
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(event) => setFormData((current) => ({
                      ...current,
                      confirmPassword: event.target.value,
                    }))}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    required
                  />
                </div>

                <Button type="submit" className="w-full btn-gold text-black" disabled={loading}>
                  {loading
                    ? t("auth.resettingPassword", "Resetting password...")
                    : t("auth.resetPassword", "Reset Password")}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
